/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import { css } from "@emotion/react";
import * as authSelectors from "../auth/authSelectors";
import {
  SalesChannelIntegration,
  SalesChannelEgressConnectionTrigger,
  SalesChannelPlatform,
} from "./models/sales-channel";
import { useGetSalesChannel } from "./service/shop-api";
import { Drawer } from "../../components/Drawer";
import { ButtonExternalLink } from "../../components/NewButtons";
import { DrawerSubHeader } from "../../components/DrawerSubHeader";
import { Divider } from "../../components/Divider";
import { Shop } from "./model/ShopSuccessResponse";
import { StyledInfoBox } from "../../components/StyledInfoBox";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { useHandleFetchError } from "../../error-handler";
import { SpinnerBlock } from "../common/Spinner";
import { heapsGoAdminSalesChannelUrl } from "./heapsgo-admin-links";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  salesChannelId: string;
  shop?: Shop;
};

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;

const valueStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const platformLabels: Record<SalesChannelPlatform, string> = {
  Foodora: "Foodora",
  JET: "Just Eat",
  Wolt: "Wolt",
  HeapsGo: "HeapsGo",
  "": "",
};

const egressTriggerLabels: Record<
  SalesChannelEgressConnectionTrigger,
  string
> = {
  accepted: "Accepted",
  ready: "Ready",
  manufacturing: "Cooking",
};

const Value = ({ label, value }: { label: string; value: string }) => (
  <div css={valueStyle}>
    <StyledText color={colors.textSecondary} className="bodySubtextWide mb-0">
      {label}
    </StyledText>
    <StyledText color={colors.textPrimary} className="bodyTextSemibold mb-0">
      {value}
    </StyledText>
  </div>
);

export const SalesChannelDrawer = ({
  isOpen,
  onClose,
  salesChannelId,
  shop,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  // Fetch on every open and on returning to the tab: sales channels are
  // edited in the new admin, so a cached copy can be stale. The response
  // carries the integration names and the egress connections too.
  const {
    data: salesChannel,
    isLoading,
    error: salesChannelError,
  } = useGetSalesChannel(organizationKeyName, salesChannelId, isOpen);

  useHandleFetchError(salesChannelError);

  const egressConnections = salesChannel?.egressConnections || [];

  const renderIntegration = (integration?: SalesChannelIntegration | null) =>
    integration?.id ? (
      <>
        <Value
          label="Integration"
          value={integration.name || "Unknown integration"}
        />
        <Value label="Reference" value={integration.reference || "-"} />
      </>
    ) : (
      <StyledText color={colors.textPrimary} className="bodyTextRegular mb-0">
        None
      </StyledText>
    );

  return (
    <Drawer
      title="Sales channel"
      width={500}
      styles={{
        body: {
          padding: "0px",
        },
      }}
      buttons={
        shop?.id && (
          <ButtonExternalLink
            href={heapsGoAdminSalesChannelUrl(
              organizationKeyName,
              shop.id,
              salesChannelId
            )}
          >
            Edit in the new admin
          </ButtonExternalLink>
        )
      }
      open={isOpen}
      onClose={onClose}
    >
      {salesChannelError ? (
        <div css={sectionStyle}>
          <StyledInfoBox title="The sales channel could not be loaded" />
        </div>
      ) : isLoading || !salesChannel ? (
        <SpinnerBlock />
      ) : (
        <>
          <div css={sectionStyle}>
            <StyledInfoBox
              title="Sales channels are managed in the new admin"
              subtitle="This is a read-only view. Use “Edit in the new admin” to change the sales channel or its integrations."
            />
            <Value label="Name" value={salesChannel.name} />
            <Value
              label="Platform"
              value={
                platformLabels[salesChannel.platform] || salesChannel.platform
              }
            />
          </div>
          <Divider />
          <div css={sectionStyle}>
            <DrawerSubHeader
              header="Menu push integration"
              subtext="Pushes the menu to the external platform."
            />
            {renderIntegration(salesChannel.menuPushIntegration)}
          </div>
          <Divider />
          <div css={sectionStyle}>
            <DrawerSubHeader
              header="Incoming orders integration"
              subtext="Brings orders from the external platform to the restaurant tablet."
            />
            {renderIntegration(salesChannel.orderIngestIntegration)}
          </div>
          <Divider />
          <div css={sectionStyle}>
            <DrawerSubHeader
              header="Outgoing integrations"
              subtext="Send orders and other data to external systems."
            />
            {egressConnections.length === 0 && (
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-0"
              >
                None
              </StyledText>
            )}
            {egressConnections.map((egressConnection) => (
              <div css={valueStyle} key={egressConnection.id}>
                <StyledText
                  className="bodyTextSemibold mb-0"
                  color={colors.textPrimary}
                >
                  {egressConnection.integrationName}
                </StyledText>
                {egressConnection.description && (
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    {egressConnection.description}
                  </StyledText>
                )}
                {egressConnection.trigger && (
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    Trigger:{" "}
                    {egressTriggerLabels[egressConnection.trigger] ||
                      egressConnection.trigger}
                  </StyledText>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
};
